if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      const idInput = this.form.querySelector('[name=id]');
      if (idInput && idInput.value) {
        idInput.disabled = false;
      }
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.cart = document.querySelector('cart-items') || document.querySelector('cart-drawer');
    }

    #getSubmitButton() {
      return this.form?.querySelector('[type="submit"]') ?? null;
    }

    /**
     * Subscription apps (e.g. Appstle) often render selling_plan inputs in an @app block outside this
     * theme's minimal product form. Collect those fields from the same .product column so cart/add
     * receives selling_plan on FormData and JSON flows.
     */
    #getSellingPlanFieldsFromOutsideForm() {
      const out = {};
      const root = this.closest('.product');

      if (!root || !this.form) {
        return out;
      }

      for (const el of root.querySelectorAll('input[name], select[name], textarea[name]')) {
        if (this.form.contains(el)) {
          continue;
        }

        if (el.disabled) {
          continue;
        }

        const nm = el.name;

        if (!nm || !nm.toLowerCase().includes('selling_plan')) {
          continue;
        }

        if (el.type === 'radio' || el.type === 'checkbox') {
          if (!el.checked) {
            continue;
          }
        }

        const val = String(el.value ?? '').trim();

        if (val === '') {
          continue;
        }

        out[nm] = val;
      }

      return out;
    }

    #applySellingPlanFromOutsideFormToLineItem(item) {
      const fields = this.#getSellingPlanFieldsFromOutsideForm();
      const raw = fields.selling_plan;

      if (raw == null || String(raw).trim() === '') {
        return;
      }

      const num = Number.parseInt(String(raw), 10);
      item.selling_plan = Number.isNaN(num) ? String(raw).trim() : num;
    }

    #mergeSellingPlanFieldsIntoFormData(formData) {
      const fields = this.#getSellingPlanFieldsFromOutsideForm();

      for (const [name, value] of Object.entries(fields)) {
        formData.set(name, value);
      }
    }

    #getLineItemPropertiesFromForm() {
      const props = {};

      if (!this.form) {
        return props;
      }

      for (const el of this.form.querySelectorAll('[name^="properties["]')) {
        const name = el.getAttribute('name');
        const match = name?.match(/^properties\[(.+)\]$/);

        if (!match) {
          continue;
        }

        const val = String(el.value ?? '').trim();

        if (val === '') {
          continue;
        }

        props[match[1]] = val;
      }

      return props;
    }

    #applyLineItemPropertiesFromForm(item) {
      const props = this.#getLineItemPropertiesFromForm();
      const keys = Object.keys(props);

      if (keys.length === 0) {
        return;
      }

      item.properties = { ...(item.properties ?? {}), ...props };
    }

    onSubmit(evt) {
      evt.preventDefault();

      const submitButton = this.#getSubmitButton();
      const idInput = this.form.querySelector('[name="id"]');
      if (!idInput || !idInput.value || idInput.disabled) {
        return;
      }

      if (!submitButton) {
        return;
      }

      if (submitButton.disabled) {
        return;
      }

      if (submitButton.getAttribute('aria-disabled') === 'true') return;

      this.error = false;
      this.#clearAddError();

      const personalization = this.closest('.product')?.querySelector('product-personalisation');
      const photoStitch = this.closest('.product')?.querySelector('product-photo-stitch');

      if (
        personalization &&
        typeof personalization.requiresPersonalizationBeforeCart === 'function' &&
        personalization.requiresPersonalizationBeforeCart()
      ) {
        return;
      }

      if (
        photoStitch &&
        typeof photoStitch.requiresPhotoStitchBeforeCart === 'function' &&
        photoStitch.requiresPhotoStitchBeforeCart()
      ) {
        return;
      }

      submitButton.setAttribute('aria-disabled', true);

      const cartDrawer = document.querySelector('cart-drawer');
      this._flySession = null;
      this._pendingCartCountUpdate = null;
      this.#beginOptimisticFly();

      // Photo stitch: FormData (files) then fee line with quantity = image count
      const photoStitchData = photoStitch?.getPayload?.() ?? null;

      if (photoStitchData) {
        this.#submitPhotoStitch(photoStitchData, photoStitch, submitButton, cartDrawer);
        return;
      }

      // Required photo stitch must never fall through to a plain add
      if (
        photoStitch &&
        typeof photoStitch.requiresPhotoStitchBeforeCart === 'function' &&
        photoStitch.requiresPhotoStitchBeforeCart()
      ) {
        this.error = true;
        this.#finishFlySession();
        this.#finishSubmitUi(submitButton, cartDrawer);
        return;
      }

      // Check for Personalization Bundle (same product column as this form)
      const bundleData = personalization?.getPayload?.() ?? null;

      if (bundleData) {
        // --- BUNDLED SUBMISSION (JSON) ---
        const mainVariantId = this.form.querySelector('[name=id]').value;
        // Destructure: Extract 'For Product' for the Fee, keep the rest for Main
        const { 'For Product': forProduct, ...mainProperties } = bundleData.properties;
        const uniqueId = mainProperties._unique_id; // Keep reference for linking

        const items = [
          {
            id: mainVariantId,
            quantity: 1,
            properties: mainProperties, // Contains: Text, Settings, _unique_id
          },
        ];

        this.#applyLineItemPropertiesFromForm(items[0]);
        this.#applySellingPlanFromOutsideFormToLineItem(items[0]);

        if (bundleData.feeId != null && bundleData.feeId !== '') {
          items.push({
            id: bundleData.feeId,
            quantity: 1,
            properties: {
              _related_main_id: String(uniqueId),
              _fee_per_unit: '1',
              'For Product': forProduct,
            },
          });
        }

        const payload = {
          items,
          sections: ['cart-drawer', 'header'],
          sections_url: window.location.pathname,
        };

        // Fetch with JSON body
        const config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript',
          },
          body: JSON.stringify(payload),
        };

        fetch(`${window.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.#handleCartAddFailure(response);
              return;
            }

            this.error = false;

            this.updateCartDrawerAndHeader(response, cartDrawer);

            // RESET Personalization Form
            if (personalization) personalization.reset();

            this.dispatchEvent(
              new CustomEvent('product-form:submit:success', {
                bubbles: true,
                detail: { response },
              }),
            );
          })
          .catch((e) => {
            console.error(e);
            this.#handleCartAddFailure(e);
          })
          .finally(() => {
            this.#finishFlySession();
            this.#finishSubmitUi(submitButton, cartDrawer);
          });

        return; // EXIT EARLY
      }

      // Required personalisation must never fall through to a plain add
      if (
        personalization &&
        typeof personalization.requiresPersonalizationBeforeCart === 'function' &&
        personalization.requiresPersonalizationBeforeCart()
      ) {
        this.error = true;
        this.#finishFlySession();
        this.#finishSubmitUi(submitButton, cartDrawer);
        return;
      }

      // --- STANDARD FORM SUBMISSION ---
      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
      };

      const formData = new FormData(this.form);
      this.#mergeSellingPlanFieldsIntoFormData(formData);
      config.body = formData;

      formData.append('sections', 'cart-drawer,header');
      formData.append('sections_url', window.location.pathname);

      fetch(`${window.routes.cart_add_url}`, config)
        .then(async (response) => {
          let json;

          try {
            json = await response.json();
          } catch {
            throw new Error(`Network response was not ok: ${response.status}`);
          }

          if (json?.status) {
            this.#handleCartAddFailure(json);
            return;
          }

          if (!response.ok) {
            this.#handleCartAddFailure(json);
            return;
          }

          this.error = false;

          this.updateCartDrawerAndHeader(json, cartDrawer);

          this.dispatchEvent(
            new CustomEvent('product-form:submit:success', {
              bubbles: true,
              detail: { response: json },
            }),
          );
        })
        .catch((e) => {
          console.error(e);
          this.#handleCartAddFailure(e);
        })
        .finally(() => {
          this.#finishFlySession();
          this.#finishSubmitUi(submitButton, cartDrawer);
        });
    }

    /**
     * Native Shopify file uploads require multipart FormData.
     * Fee product is added in a second request (quantity = number of images).
     * If the fee add fails (after one retry), the main line is removed so the cart
     * never keeps photos without the matching fee.
     */
    #submitPhotoStitch(photoStitchData, photoStitch, submitButton, cartDrawer) {
      const mainVariantId = this.form.querySelector('[name=id]')?.value;
      const { feeId, feeQuantity, files, properties } = photoStitchData;
      const uniqueId = properties?._unique_id;
      const forProduct = properties?.['For Product'];
      let mainAdded = false;
      let bundleComplete = false;
      let latestResponse = null;
      let mainLineKey = null;

      this.error = false;
      this.#setSubmitProgress(
        submitButton,
        submitButton?.dataset.uploadingLabel || 'Uploading photos…',
      );

      const formData = new FormData();
      formData.append('id', mainVariantId);
      formData.append('quantity', '1');

      if (properties) {
        for (const [key, value] of Object.entries(properties)) {
          if (value == null || value === '') continue;
          // Fee-only property — mirrors text personalisation bundle split.
          if (key === 'For Product') continue;
          formData.append(`properties[${key}]`, String(value));
        }
      }

      if (files) {
        for (const [key, file] of Object.entries(files)) {
          if (!file) continue;
          formData.append(`properties[${key}]`, file, file.name);
        }
      }

      const extraProps = this.#getLineItemPropertiesFromForm();

      for (const [key, value] of Object.entries(extraProps)) {
        formData.append(`properties[${key}]`, value);
      }

      this.#mergeSellingPlanFieldsIntoFormData(formData);
      formData.append('sections', 'cart-drawer,header');
      formData.append('sections_url', window.location.pathname);

      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
        body: formData,
      };

      const addFee = () => {
        const feePayload = {
          items: [
            {
              id: feeId,
              quantity: feeQuantity,
              properties: {
                _related_main_id: String(uniqueId),
                _fee_per_unit: String(feeQuantity),
                'For Product': forProduct,
              },
            },
          ],
          sections: ['cart-drawer', 'header'],
          sections_url: window.location.pathname,
        };

        return fetch(`${window.routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript',
          },
          body: JSON.stringify(feePayload),
        }).then((feeResponse) => feeResponse.json());
      };

      const removeMainLine = () => {
        if (!mainLineKey) return Promise.resolve(null);

        return fetch(`${window.routes.cart_change_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript',
          },
          body: JSON.stringify({
            id: mainLineKey,
            quantity: 0,
            sections: ['cart-drawer', 'header'],
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .catch((error) => {
            console.error(error);
            return null;
          });
      };

      fetch(`${window.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.#handleCartAddFailure(response);
            return null;
          }

          mainAdded = true;
          latestResponse = response;
          mainLineKey = response.key || null;

          if (feeId == null || feeId === '' || !feeQuantity) {
            bundleComplete = true;
            return response;
          }

          this.#setSubmitProgress(
            submitButton,
            submitButton?.dataset.addingLabel || 'Adding to cart…',
          );

          return addFee()
            .then((feeJson) => {
              if (!feeJson.status) {
                bundleComplete = true;
                latestResponse = feeJson;
                return feeJson;
              }

              // One retry before rolling back the photo line.
              return addFee().then((retryJson) => {
                if (!retryJson.status) {
                  bundleComplete = true;
                  latestResponse = retryJson;
                  return retryJson;
                }

                console.error(retryJson.description || retryJson.message || retryJson);
                return removeMainLine().then((rollbackState) => {
                  mainAdded = false;

                  if (rollbackState && !rollbackState.errors && rollbackState.sections) {
                    this.updateCartDrawerAndHeader(rollbackState, cartDrawer);
                  }

                  this.#handleCartAddFailure(
                    this.dataset.photoStitchFeeError ||
                      retryJson.description ||
                      retryJson.message ||
                      retryJson,
                  );
                  return null;
                });
              });
            })
            .catch((feeError) => {
              console.error(feeError);

              return removeMainLine().then((rollbackState) => {
                mainAdded = false;

                if (rollbackState && !rollbackState.errors && rollbackState.sections) {
                  this.updateCartDrawerAndHeader(rollbackState, cartDrawer);
                }

                this.#handleCartAddFailure(this.dataset.photoStitchFeeError || feeError);
                return null;
              });
            });
        })
        .then((response) => {
          if (!response || !bundleComplete) return;

          this.error = false;
          this.updateCartDrawerAndHeader(response, cartDrawer);

          this.dispatchEvent(
            new CustomEvent('product-form:submit:success', {
              bubbles: true,
              detail: { response },
            }),
          );
        })
        .catch((e) => {
          console.error(e);
          this.#handleCartAddFailure(e);
        })
        .finally(() => {
          // Only clear the uploader after a full success — keep photos on fee/rollback failure.
          if (bundleComplete && photoStitch) {
            photoStitch.reset();
          }

          this.#finishFlySession();
          this.#finishSubmitUi(submitButton, cartDrawer);
        });
    }

    #getAddErrorElements() {
      const wrapper = this.querySelector('.product-form__error-message-wrapper');
      const messageEl = wrapper?.querySelector('.product-form__error-message') ?? null;

      return { wrapper, messageEl };
    }

    #clearAddError() {
      const { wrapper, messageEl } = this.#getAddErrorElements();

      if (messageEl) {
        messageEl.textContent = '';
      }

      if (wrapper) {
        wrapper.hidden = true;
      }
    }

    #showAddError(message) {
      const { wrapper, messageEl } = this.#getAddErrorElements();
      const text = String(message || '').trim();

      if (!wrapper || !messageEl || !text) return;

      messageEl.textContent = text;
      wrapper.hidden = false;
      wrapper.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    }

    #rewriteCartErrorMessage(message) {
      const text = String(message || '').trim();

      if (!text) return text;

      const normalized = text.toLowerCase();

      if (normalized.includes('maximum quantity of this item is already in your cart')) {
        return (
          this.dataset.cartMaxQuantityError ||
          'Out of stock, all remaining items are already in your cart'
        );
      }

      return text;
    }

    #cartErrorMessage(payload) {
      const fallback = this.dataset.addErrorFallback || 'Unable to add to cart. Please try again.';

      if (!payload) {
        return fallback;
      }

      if (typeof payload === 'string') {
        return this.#rewriteCartErrorMessage(payload);
      }

      if (typeof payload.description === 'string' && payload.description.trim()) {
        return this.#rewriteCartErrorMessage(payload.description);
      }

      if (typeof payload.message === 'string' && payload.message.trim()) {
        return this.#rewriteCartErrorMessage(payload.message);
      }

      return fallback;
    }

    #handleCartAddFailure(payload) {
      this.error = true;
      const message = this.#cartErrorMessage(payload);
      console.error(message);
      this.#showAddError(message);
    }

    #getSubmitLabelEl(submitButton) {
      return submitButton?.querySelector(':scope > span') ?? null;
    }

    #setSubmitProgress(submitButton, labelText) {
      if (!submitButton) return;

      const labelEl = this.#getSubmitLabelEl(submitButton);

      if (labelEl && labelText) {
        if (submitButton.dataset.labelBackup == null) {
          submitButton.dataset.labelBackup = labelEl.innerHTML;
        }

        labelEl.textContent = labelText;
      }

      submitButton.classList.add('loading');
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
    }

    #restoreSubmitLabel(submitButton) {
      if (!submitButton || submitButton.dataset.labelBackup == null) return;

      const labelEl = this.#getSubmitLabelEl(submitButton);

      if (labelEl) {
        labelEl.innerHTML = submitButton.dataset.labelBackup;
      }

      delete submitButton.dataset.labelBackup;
    }

    #beginOptimisticFly() {
      if (typeof window.startOptimisticFlyToCart !== 'function') {
        this.#showSubmitLoading();
        return;
      }

      this._flySessionPromise = window.startOptimisticFlyToCart(this).then((session) => {
        this._flySession = session ?? null;

        if (!session) {
          this.#showSubmitLoading();
        }

        if (session && this._pendingCartCountUpdate) {
          session.succeed(this._pendingCartCountUpdate);
          this._pendingCartCountUpdate = null;
        }

        return session;
      });
    }

    #showSubmitLoading() {
      const submitButton = this.#getSubmitButton();

      if (!submitButton) {
        return;
      }

      submitButton.classList.add('loading');
      submitButton.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
    }

    #finishFlySession() {
      if (this.error) {
        this._flySession?.fail();
        this._pendingCartCountUpdate = null;
      }

      this._flySession = null;
      this._flySessionPromise = null;
    }

    #finishSubmitUi(submitButton, cartDrawer) {
      if (submitButton) {
        this.#restoreSubmitLabel(submitButton);
        submitButton.classList.remove('loading');
        submitButton.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        submitButton.removeAttribute('aria-disabled');
      }

      if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

      if (cartDrawer) {
        cartDrawer.classList.remove('is-loading');
      }
    }

    updateCartDrawerAndHeader(response, cartDrawer) {
      const pending = scheduleCartSectionsUpdateFromResponse(response, {
        error: this.error,
        flySession: this._flySession,
        flySessionPromise: this._flySessionPromise,
        hostEl: this,
      });

      if (pending) {
        this._pendingCartCountUpdate = pending;
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
